import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, MapPin, Send, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/site/Reveal";
import { submitContact } from "@/lib/api";

const BUDGETS = ["< $10k", "$10k – $50k", "$50k – $100k", "$100k+"];
const SERVICES = ["Custom Software", "Web & Mobile", "Cloud & DevOps", "AI / ML", "UI/UX Design", "Consulting"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSuccess(true);
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", company: "", budget: "", service: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-20">
      <section className="mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-12">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · Contact
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Let's build something <span className="text-gradient">remarkable</span>.
          </h1>
          <p className="mt-7 text-xl text-zinc-400 max-w-2xl">
            Tell us what you're working on. We respond within one business day — no chatbots, no
            automated funnels.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-12 mt-12 grid lg:grid-cols-12 gap-8">
        {/* Form */}
        <Reveal className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-3xl glass border-white/[0.08] p-8 md:p-10 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Name" required>
                <input data-testid="contact-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
              </Field>
              <Field label="Email" required>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" />
              </Field>
              <Field label="Company">
                <input data-testid="contact-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Where you work" />
              </Field>
              <Field label="Service">
                <select data-testid="contact-service" value={form.service} onChange={update("service")} className={inputCls}>
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Budget range">
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`contact-budget-${b}`}
                    onClick={() => setForm({ ...form, budget: b })}
                    className={`rounded-full px-4 py-1.5 text-xs font-medium border transition ${form.budget === b ? "bg-nexus-cyan text-nexus-ink border-nexus-cyan" : "border-white/10 bg-white/[0.03] text-zinc-300 hover:bg-white/[0.06]"}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Tell us about your project" required>
              <textarea
                data-testid="contact-message"
                required
                rows={6}
                value={form.message}
                onChange={update("message")}
                className={`${inputCls} resize-y`}
                placeholder="What are you trying to build? What's the timeline?"
              />
            </Field>

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-zinc-500">By submitting, you agree to our privacy policy.</p>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-6 py-3 text-sm hover:brightness-110 transition disabled:opacity-60"
              >
                {loading ? "Sending…" : success ? "Sent!" : "Send message"}
                {success ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </form>
        </Reveal>

        {/* Contact methods */}
        <Reveal delay={0.1} className="lg:col-span-5 space-y-5">
          <ContactCard icon={Mail} label="Email" value="admin@fullstackendeavours.in" href="mailto:admin@fullstackendeavours.in" testid="contact-method-email" />
          <ContactCard icon={Phone} label="Phone" value="+1 (555) 123-4567" href="tel:+15551234567" testid="contact-method-phone" />
          <ContactCard icon={MessageCircle} label="WhatsApp" value="Chat with us" href="https://wa.me/15551234567" testid="contact-method-whatsapp" />
          <div className="rounded-2xl glass border-white/[0.08] p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 border border-white/10">
                <MapPin className="h-4 w-4 text-nexus-cyan" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Studio</span>
            </div>
            <p className="font-cabinet text-lg">228 Hudson Street, Suite 4</p>
            <p className="text-zinc-400 text-sm">New York, NY 10013, USA</p>
            <p className="text-xs text-zinc-500 mt-4">Distributed team — also based in Lisbon & Bengaluru.</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

const inputCls = "w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm outline-none focus:border-nexus-cyan/60 focus:bg-white/[0.05] transition placeholder:text-zinc-600";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 inline-block">
        {label}{required && <span className="text-nexus-cyan ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactCard({ icon: Icon, label, value, href, testid }) {
  return (
    <a href={href} data-testid={testid} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block rounded-2xl glass border-white/[0.08] p-7 hover:bg-white/[0.04] transition group">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 grid place-items-center rounded-lg bg-white/5 border border-white/10 group-hover:border-nexus-cyan/40 transition">
            <Icon className="h-4 w-4 text-nexus-cyan" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="font-cabinet text-lg mt-0.5">{value}</div>
          </div>
        </div>
        <ArrowUpRight className="h-5 w-5 text-zinc-500 group-hover:text-nexus-cyan transition" />
      </div>
    </a>
  );
}
